/**
 * ChangePhoneScreen.jsx — TEST 3.01: Settings -> Mobile. Visit reminders and sign-in codes move to a new number
 * only after a code texted to the NEW number is entered.
 *
 * The same two routes and controls as the SSO "add your mobile" step (pages/onboarding/OnboardingPhone.jsx:
 * Turnstile, the verbatim TCPA opt-in, PROD-807, PROD-1154's current-code field), in a Settings screen with Back.
 * That onboarding step is left exactly as it is for a FIRST number (it never sends a re-auth or a change token).
 *
 *   REPLACING a number (the account holds one, or the server says so with 401 reauth_required):
 *     send-code carries "confirm it's you" (components/ContactReauth) and answers { changeToken } (15 minutes);
 *     verify carries that changeToken and answers a fresh session `token`, which authAPI.contactPhoneVerify stores
 *     at once (the server bumped token_version: the old session and the other devices are signed out).
 *   A FIRST number: no re-auth and no token, as onboarding.
 * Neither call sends the scoped 2FA-setup bearer: this screen is reached only with a session, and a setup bearer
 * would get no fresh session back.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check } from 'lucide-react'
import useMenuBack from '../../hooks/useMenuBack'
import { ResponsiveWrapper } from '../../components/layout'
import { authAPI, notificationsAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import Turnstile from '../../components/Turnstile'
import SmsConsentText from '../../components/SmsConsentText'
import ContactReauth, { useContactReauth } from '../../components/ContactReauth'
import CurrentFactorField, { LockNotice, useLockCountdown } from '../../components/CurrentFactorField'
import { currentCodeMayBeSpent, currentCodeProblem, currentFactorRefusal, normalizeCurrentCode, refusalSentence } from '../../utils/currentFactor'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import { isProviderCancel } from '../../utils/ssoReauth'
import { apiErrorCode } from '../../utils/apiErrorMessage'
import { contactRefusal, displayPhone } from '../../utils/contactChange'

const INPUT = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none'
const PRIMARY = { width: '100%', padding: '13px 16px', borderRadius: 14, border: 'none', fontSize: 15, fontWeight: 700, color: 'white' }
const digitsOf = (v) => (v || '').replace(/\D/g, '')
const usNumber = (v) => {
  const d = digitsOf(v)
  return d.length === 10 || (d.length === 11 && d.startsWith('1'))
}

export default function ChangePhoneScreen() {
  const { t } = useI18n()
  const goBack = useMenuBack()
  const navigate = useNavigate()
  const accountPhone = useUserStore((s) => s.user?.phone) || ''
  const reauth = useContactReauth()
  const [serverAskedReauth, setServerAskedReauth] = useState(false)
  // R301B: ANY stored value counts as a number to replace (a short legacy "555-1234" too): the server re-auths every
  //   send for an account that holds one, so asking first saves a refused round trip.
  const replacing = String(accountPhone || '').trim() !== '' || serverAskedReauth
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [sent, setSent] = useState(false)
  const [changeToken, setChangeToken] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  // TEST 3.01 M2: the box starts from today's text-reminder setting (profiles.settings via /notification-preferences, the
  // same source as the Settings SMS toggle). Until it is read, or if the read fails, it starts unticked. A tap before the
  // read arrives wins: the late answer never overrides the patient's choice.
  const [smsOptIn, setSmsOptInRaw] = useState(false)
  const smsTouched = useRef(false)
  const setSmsOptIn = (v) => { smsTouched.current = true; setSmsOptInRaw(v) }
  useEffect(() => {
    let active = true
    ;(async () => {
      try {
        const res = await notificationsAPI.getPreferences()
        if (active && !smsTouched.current && res?.masterToggles?.sms === true) setSmsOptInRaw(true)
      } catch (e) {
        // setting unknown: the box stays unticked
      }
    })()
    return () => { active = false }
  }, [])
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileKey, setTurnstileKey] = useState(0)
  const [currentAsk, setCurrentAsk] = useState(false)
  const [currentCode, setCurrentCode] = useState('')
  const [currentError, setCurrentError] = useState(null)
  const [codeSpent, setCodeSpent] = useState(false)
  const [lockSeconds, startLock] = useLockCountdown()
  const [done, setDone] = useState(null) // { phone, signedOut }
  // R301P L3: the field at fault takes the focus after an error, once the screen is not busy (inputs are disabled then).
  const phoneField = useRef(null)
  const codeField = useRef(null)
  const [focusAsk, setFocusAsk] = useState(null)
  const askFocus = (field) => setFocusAsk((p) => ({ field, n: (p?.n || 0) + 1 }))
  useEffect(() => {
    if (!focusAsk || busy) return
    const el = focusAsk.field === 'code' ? codeField.current : phoneField.current
    if (el && !el.disabled) el.focus()
    setFocusAsk(null)
  }, [focusAsk, busy])

  const changePhone = (v) => {
    setPhone(v)
    setError(null)
    if (sent) { setSent(false); setCode(''); setCodeSpent(false); setChangeToken(null) }
  }

  /** Shows the refusal's sentence and returns what contactRefusal read (null = not a contact refusal). */
  const showRefusal = (e, opts) => {
    const r = contactRefusal(e, opts)
    if (!r) return null
    if (r.kind === 'locked') { startLock(r.seconds); setError(null); return r }
    if (r.kind === 'reauth') { reauth.refused(r); setServerAskedReauth(true) }
    setError(t(r.key))
    return r
  }

  const send = async () => {
    if (!usNumber(phone) || busy || lockSeconds) return
    if (!turnstileToken) { setError(t('auth.verifyHuman')); return }
    if (replacing && !reauth.ready) return
    setBusy(true); setError(null)
    try {
      const proof = replacing ? await reauth.prove() : null
      const res = await authAPI.contactPhoneSendCode(phone, turnstileToken, proof)
      setChangeToken(res?.changeToken || null)
      setSent(true)
      setCode(''); setCodeSpent(false)
    } catch (e) {
      if (isProviderCancel(e) || e?.message === 'cancelled') setError(t('settingsMisc.reauthCancelled'))
      else if (isSmsRefused(e) && smsRefusedSentence(e)) { setError(smsRefusedSentence(e)); askFocus('phone') }
      else {
        const r = showRefusal(e)
        if (r && (r.kind === 'reauth' || r.kind === 'locked')) { setSent(false); setChangeToken(null) }
        // R301P L2: an unknown code, or a 401 with none: the translated sentence and the status, never the server's text.
        if (!r) setError(`${t('onboarding.phone.sendFailed')}${apiErrorCode(e)}`)
        if (!r || r.kind === 'key') askFocus('phone')
      }
    } finally {
      // Turnstile tokens are single-use: a resend gets a fresh challenge.
      setTurnstileToken(null); setTurnstileKey((k) => k + 1)
      setBusy(false)
    }
  }

  /**
   * R301P L1: the password is not kept after a send, so a resend of a REPLACEMENT with the password method goes back
   * to the number step (the number stays) and asks for it again. A first number, or an Apple / Google proof (a fresh
   * sign-in inside this tap), resends at once.
   */
  const resend = () => {
    if (!replacing || reauth.ready) { send(); return }
    setError(null); setSent(false); setCode(''); setCodeSpent(false); setChangeToken(null)
  }

  const verify = async () => {
    if (!/^\d{6}$/.test(code) || busy || lockSeconds) return
    if (codeSpent) { setError(t('onboarding.phone.codeUsed')); return }
    const currentProblem = currentCodeProblem(currentAsk, currentCode)
    if (currentProblem) { setCurrentError(refusalSentence(t, currentProblem) || null); return }
    const sentCurrent = currentAsk ? normalizeCurrentCode(currentCode) : undefined
    setBusy(true); setError(null); setCurrentError(null)
    try {
      const res = await authAPI.contactPhoneVerify(phone, code, smsOptIn, sentCurrent, changeToken)
      const e164 = `+1${digitsOf(phone).slice(-10)}`
      const st = useUserStore.getState()
      if (st.user) st.setUser({ ...st.user, phone: e164 })
      setDone({ phone: displayPhone(e164), signedOut: !!res?.token, smsOff: smsOptIn !== true })
    } catch (e) {
      // PROD-1154, as OnboardingPhone: with a current code sent, a failure may come after both codes were used.
      const spent = Boolean(sentCurrent && currentCodeMayBeSpent(e))
      if (spent) { setCurrentCode(''); setCode(''); setCodeSpent(true) }
      const refused = currentFactorRefusal(e)
      if (refused) {
        setCurrentAsk(true)
        if (refused.locked) startLock(refused.seconds)
        else setCurrentError(refusalSentence(t, refused.code) || null)
        if (spent) setError(t('onboarding.phone.codeUsed'))
      } else if (isSmsRefused(e) && smsRefusedSentence(e)) {
        setError(smsRefusedSentence(e))
        askFocus('phone')
      } else {
        const r = showRefusal(e, { atVerify: true })
        if (!r) {
          // R301P L2: the translated sentence and the status, never the server's text.
          const what = `${t('errors2.somethingWentWrong')}${apiErrorCode(e)}`
          setError(spent ? `${what} ${t('onboarding.phone.codeUsed')}` : what)
          askFocus('code')
        } else if (r.kind === 'restart' || r.kind === 'reauth') {
          // The pending change is gone (taken, unchanged, or no change token): start again from the number.
          // R301B: phone_in_use arrives HERE, after the code (send-code texts a held number like a free one). That
          //   code is spent: the patient types a different number, requests a new code, and re-auths again for a
          //   replacement (the password is not kept, R301P L1).
          setSent(false); setCode(''); setChangeToken(null)
          if (r.kind === 'restart') askFocus('phone')
        } else if (r.kind === 'key') askFocus('code')
      }
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <ResponsiveWrapper className="bg-white shadow-xl">
        <div className="screen-min-h bg-white flex flex-col items-center justify-center p-8" data-contact-done="phone">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <Check className="w-8 h-8 text-green-600" strokeWidth={2.5} />
          </div>
          <p className="text-gray-700 text-center font-semibold">{t('contact.phoneDone', { phone: done.phone })}</p>
          {done.signedOut && <p className="text-gray-500 text-center text-sm mt-3">{t('contact.signedOutElsewhere')}</p>}
          {done.smsOff && <p className="text-gray-500 text-center text-sm mt-3" data-contact-sms-off="1">{t('contact.smsOffAfterChange')}</p>}
          <button type="button" onClick={() => navigate('/settings', { replace: true })} style={{ ...PRIMARY, background: '#46A8F9', marginTop: 24 }}>
            {t('common.done')}
          </button>
        </div>
      </ResponsiveWrapper>
    )
  }

  const canSend = !busy && !lockSeconds && usNumber(phone) && !!turnstileToken && (!replacing || reauth.ready)
  const canVerify = !busy && !lockSeconds && /^\d{6}$/.test(code)
  return (
    <ResponsiveWrapper className="bg-white shadow-xl">
      <div className="screen-min-h bg-white flex flex-col">
        <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-3">
          <button type="button" onClick={() => goBack()} className="p-2 -ml-2 text-gray-600" aria-label={t('common.back')}>
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-orella-700">{t('contact.phoneTitle')}</h1>
        </div>
        <div className="flex-1 px-4 pb-8 overflow-auto">
          <p className="text-sm text-gray-500 mt-1 mb-5">{t('contact.phoneIntro')}</p>
          <label htmlFor="contact-phone-number" className="block text-sm text-gray-500 mb-1.5">{t('onboarding.welcome.phone')}</label>
          <input id="contact-phone-number" ref={phoneField} type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" value={phone} disabled={busy}
            onChange={(e) => changePhone(e.target.value)} className={INPUT} />
          {replacing && !sent && <ContactReauth reauth={reauth} disabled={busy} />}
          {sent && (
            <div style={{ marginTop: 14 }}>
              <label htmlFor="contact-phone-code" className="block text-sm text-gray-500 mb-1.5">{t('onboarding.phone.codeLabel')}</label>
              <input id="contact-phone-code" ref={codeField} inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} disabled={busy}
                onChange={(e) => setCode(digitsOf(e.target.value).slice(0, 6))} className={INPUT} />
              <p style={{ fontSize: 12, color: '#64748B', marginTop: 4 }}>{t('onboarding.phone.codeHint')}</p>
            </div>
          )}
          {/* PROD-1154: SMS sign-in only - a CURRENT code, texted to the current number (asked by the API's 403) */}
          {sent && currentAsk && (
            <div style={{ marginTop: 10 }} data-phone-current-code="1">
              {currentError && <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', marginBottom: 6 }}>{currentError}</p>}
              <CurrentFactorField
                value={currentCode}
                onChange={(v) => { setCurrentCode(v); setCurrentError(null) }}
                method="sms"
                disabled={busy || !!lockSeconds}
                autoFocus
                onEnter={() => { verify() }}
              />
            </div>
          )}
          <div className="flex items-start gap-2.5 cursor-pointer py-1" style={{ marginTop: 10 }}
            role="checkbox" aria-checked={smsOptIn} tabIndex={0}
            onClick={() => setSmsOptIn((v) => !v)}
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setSmsOptIn((v) => !v) } }}>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
              style={{ borderRadius: 6, backgroundColor: smsOptIn ? '#46A8F9' : 'white', border: smsOptIn ? 'none' : '2px solid #CBD5E1' }}>
              {smsOptIn && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>}
            </div>
            <SmsConsentText changeScreen />
          </div>
          {turnstileToken === null && (
            <div className="flex justify-center" style={{ minHeight: 65, margin: '8px 0 4px' }}>
              <Turnstile
                key={turnstileKey}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>
          )}
          <div style={{ marginTop: 8 }}><LockNotice seconds={lockSeconds} /></div>
          {error && <p role="alert" style={{ fontSize: 12.5, color: '#DC2626', marginTop: 8 }}>{error}</p>}
          {sent && (
            <button type="button" onClick={resend} disabled={busy || !turnstileToken} data-phone-code-spent={codeSpent ? '1' : undefined}
              style={{ background: 'none', border: 0, color: codeSpent ? '#15354F' : '#64748B', fontWeight: codeSpent ? 600 : undefined, fontSize: 13, textDecoration: 'underline', padding: '6px 0' }}>
              {t('auth.resendCode')}
            </button>
          )}
          <div style={{ marginTop: 16 }}>
            {sent ? (
              <button type="button" onClick={verify} disabled={!canVerify} style={{ ...PRIMARY, background: canVerify ? '#46A8F9' : '#A5D4FC' }}>
                {t('onboarding.phone.verify')}
              </button>
            ) : (
              <button type="button" onClick={send} disabled={!canSend} style={{ ...PRIMARY, background: canSend ? '#46A8F9' : '#A5D4FC' }}>
                {t('onboarding.phone.send')}
              </button>
            )}
          </div>
        </div>
      </div>
    </ResponsiveWrapper>
  )
}
